// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { ModalDialogBox } from './modal-dialog-box';
import { render, createElement, addStatusMessage, clearStatusMessages } from './common';

/**
 * @class Jsw.PopupForm
 * @extends Jsw.ModalDialogBox
 */
export const PopupForm = Class.create(ModalDialogBox, {
    _initConfiguration($super, config) {
        config = Object.extend({
            cls: 'popup-panel',
        }, config || {});
        $super(config);

        this._popupContentAreaId = `${this._id}-popup-content-area`;
        this._titleAreaId = `${this._id}-title-area`;
        this._hintAreaId = `${this._id}-hint-area`;
        this._hint1AreaId = `${this._id}-hint1-area`;
        this._boxAreaId = `${this._id}-box-area`;
        this._contentAreaId = `${this._id}-content-area`;

        this._actionButtonsId = `${this._id}-action-buttons`;
        this._leftActionButtonsAreaId = `${this._id}-left-action-buttons-area`;
        this._rightActionButtonsAreaId = `${this._id}-right-action-buttons-area`;

        this._titleCls = this._getConfigParam('titleCls', '');
        this._scrollable = this._getConfigParam('scrollable');
        this._isRemoved = false;

        if (this._scrollable) {
            this._cls += ' popup-panel-scrollable';
        }
    },

    _initComponentElement($super) {
        $super();
        render(this._componentElement, this.view());
        this.show();
    },

    view() {
        const ce = createElement;
        return ce('.popup-wrapper', ce('.popup-container', [
            ce('.popup-heading', ce('.popup-heading-area', [
                ce('span.popup-heading-actions', this._getHeadingActions()),
                ce(`h2.${this._titleCls}`,
                    ce(`span#${this._titleAreaId}`, {
                        title: this._getConfigParam('title'),
                    }, this._getConfigParam('title'))
                ),
            ])),
            ce('.popup-content', ce(`#${this._popupContentAreaId}.popup-content-area`,
                this._getContentArea()
            )),
        ]));
    },

    _getHeadingActions() {
        return '';
    },

    _getContentArea() {
        const ce = createElement;
        return [
            ce(`p#${this._hint1AreaId}`),
            ce(`span#${this._hintAreaId}`),
            this._getBoxArea(),
            this._getButtonsArea(),
        ];
    },

    _getBoxArea() {
        const ce = createElement;
        return ce(`#${this._boxAreaId}.form-box`, ce('.box-area', ce('.content',
            ce(`#${this._contentAreaId}.content-area`)
        )));
    },

    _getButtonsArea() {
        const ce = createElement;
        let buttonsRow = ce(`#${this._rightActionButtonsAreaId}.field-value`, ' ');
        if (this._getConfigParam('singleRowButtons')) {
            buttonsRow = ce('.single-row', buttonsRow);
        } else {
            buttonsRow = [
                ce(`#${this._leftActionButtonsAreaId}.field-name`, ' '),
                buttonsRow,
            ];
        }
        return ce(`#${this._actionButtonsId}.btns-box`, ce('.box-area', ce('.form-row', buttonsRow)));
    },

    toggleButtonsArea(bool) {
        if (this._scrollable) {
            this._componentElement.toggleClassName('popup-panel-scrollable', bool);
        }
        $(this._actionButtonsId).toggle(bool);
    },

    setBoxType(type) {
        $(this._boxAreaId).className = type;
        if ('list-box' === type) {
            $(this._actionButtonsId).addClassName('no-separator');
            $(this._actionButtonsId).addClassName('simple-box');
        }
        if ('form-box' === type) {
            $(this._actionButtonsId).removeClassName('no-separator');
            $(this._actionButtonsId).removeClassName('simple-box');
        }
        if ('fm-box' === type) {
            $(this._boxAreaId).className = 'list-box';
            $(this._actionButtonsId).addClassName('no-separator');
        }
    },

    /**
     * @param {String} cls
     */
    setTitleType(cls) {
        $(this._titleAreaId).parentNode.className = cls;
    },

    /**
     * @param {String} text
     */
    setTitle(text) {
        $(this._titleAreaId).update(text).setAttribute('title', text);
    },

    /**
     * @param {String} text
     */
    setHint(text) {
        if (text) {
            $(this._hintAreaId).update(text);
            $(this._hintAreaId).show();
        } else {
            $(this._hintAreaId).hide();
        }
    },

    /**
     * @param {String} text
     */
    setHint1(text) {
        if (text) {
            $(this._hint1AreaId).update(text);
            $(this._hint1AreaId).show();
        } else {
            $(this._hint1AreaId).hide();
        }
    },

    /**
     * Remove this component.
     */
    remove() {
        this._isRemoved = true;
        this._renderTarget.remove();
    },

    /**
     * @param {String} title
     * @param {Function} handler
     * @param {Boolean} [isDefault]
     * @param {Boolean} [isAction]
     * @param {Object} [params]
     * @returns {Element}
     */
    addRightButton(title, handler, isDefault, isAction, params) {
        const button = this._createButton(title, handler, isDefault, isAction, params);
        $(this._rightActionButtonsAreaId).insert(button);
        return button;
    },

    /**
     * @param {String} title
     * @param {Function} handler
     * @param {Boolean} [isDefault]
     * @param {Boolean} [isAction]
     * @param {Object} [params]
     * @returns {Element}
     */
    addLeftButton(title, handler, isDefault, isAction, params) {
        const button = this._createButton(title, handler, isDefault, isAction, params);
        $(this._leftActionButtonsAreaId).insert(button);
        return button;
    },

    /**
     * Clear right buttons area.
     */
    removeRightButtons() {
        $(this._rightActionButtonsAreaId).update(' ');
    },

    /**
     * Clear left buttons area.
     */
    removeLeftButtons() {
        $(this._leftActionButtonsAreaId).update(' ');
    },

    /**
     * Update list size.
     */
    resizeList() {
        if (this._isRemoved) {
            return;
        }

        const minHeigh = 150;
        const list = $(this._contentAreaId).down('table');
        const listContainer = $(this._contentAreaId).down('.list');

        const bodyHeight = (Prototype.Browser.IE)
            ? $(document.documentElement).getHeight()
            : $(document.body).getHeight();

        const buttonsHeight = $(this._actionButtonsId).getHeight();
        const top = Position.cumulativeOffset(list)[1];
        const height = list.getHeight();
        // 100px below dialog
        let newHeight = bodyHeight - top - buttonsHeight - 100;

        if (newHeight > height) {
            listContainer.style.height = '';
            listContainer.removeClassName('with-scroll');
            return;
        }

        if (newHeight < minHeigh) {
            newHeight = minHeigh;
        }

        listContainer.style.height = `${newHeight}px`;
        listContainer.style.overflowY = 'auto';
        listContainer.style.overflowX = 'hidden';
        listContainer.addClassName('with-scroll');
    },

    /**
     * @param {String} title
     * @param {Function} handler
     * @param {Boolean} [isDefault]
     * @param {Boolean} [isAction]
     * @param {Object} [params]
     * @returns {Element}
     * @private
     */
    _createButton(title, handler, isDefault, isAction, params) {
        params = (typeof params !== 'undefined') ? params : {};

        const buttonContainer = new Element('span', params);
        buttonContainer.addClassName('btn');
        if (isAction) {
            buttonContainer.addClassName('action');
        }

        buttonContainer.update(
            `<button value="" type="${isDefault ? 'submit' : 'button'}">${title}</button>`
        );
        buttonContainer.down('button').observe(
            'click',
            this._onClick.bindAsEventListener(this, handler.bind(this))
        );

        return buttonContainer;
    },

    _updateButton(element, config) {
        $H(config).each(function (pair) {
            switch (pair.key) {
                case 'title':
                    element.down('button').update(pair.value);
                    break;
                case 'disabled':
                    element.down('button').disabled = pair.value ? true : false;
                    if (pair.value) {
                        Element.addClassName(element, 'disabled');
                    } else {
                        Element.removeClassName(element, 'disabled');
                    }
                    break;
            }
        });
    },

    _onClick(event, handler) {
        Event.stop(event);
        handler(event);
    },

    _addEvents($super) {
        $super();

        if ($(this._hintAreaId)) {
            $(this._hintAreaId).hide();
        }
        if ($(this._hint1AreaId)) {
            $(this._hint1AreaId).hide();
        }
    },

    _clearMessages() {
        const contentElement = $(this._popupContentAreaId);

        ['.msg-error', '.msg-info', '.msg-warning'].each(function (msgClass) {
            contentElement.select(msgClass).each(function (messageElement) {
                messageElement.remove();
            });
        });
    },

    _addErrorMessage(message) {
        this._addStatusMessage('error', message);
    },

    _addStatusMessage(status, message) {
        $(this._popupContentAreaId).insert(
            {
                top:
                    `<div class="msg-box msg-${status}">` +
                        '<div>' +
                            '<div>' +
                                '<div>' +
                                    '<div>' +
                                        '<div>' +
                                            `<div class="msg-content">${message}</div>` +
                                        '</div>' +
                                    '</div>' +
                                '</div>' +
                            '</div>' +
                        '</div>' +
                    '</div>',
            }
        );
    },

    _userError(error) {
        clearStatusMessages();
        addStatusMessage('error', error);
        this._close();
    },

    _internalError(error) {
        clearStatusMessages();
        addStatusMessage('error', `Internal error: ${error}`);
        this._close();
    },
});



// WEBPACK FOOTER //
// ./jsw/popup-form.js